/**
 * Preisautomatik · Übersicht (Board „Preisautomatik · Übersicht“) auf vorhandenen Daten: Kopf mit
 * Läufen und Workspace-Steuerung, To-dos aus echten Zählern, Kacheln je Apartment (inaktive
 * einklappbar) und die letzte Aktivität. Jede Zahl kommt aus `dynamic-pricing/overview` oder den
 * Strategie-Profilen; Stoppen und Freigeben brauchen Verwaltungsrecht und eine Bestätigung.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemText, useApiProblem } from '@/shared/api';
import {
  EmptyState,
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components';
import { canManage, useAuth } from '@/shared/auth';
import { useObjectScope } from '@/shared/object-scope';
import { OverviewActivity } from './OverviewActivity';
import { OverviewApartments } from './OverviewApartments';
import { OverviewHeader } from './OverviewHeader';
import { OverviewStopDialog } from './OverviewStopDialog';
import { OverviewTodos } from './OverviewTodos';
import { headerChips, shortTime } from './overviewFormat';
import { HALTED_TEXT, apartmentsLabel, headerFacts, workspaceState } from './overviewModel';
import { todos } from './todoModel';
import { useDynamicPricingOverview } from './useDynamicPricingOverview';

export function DynamicPricingOverviewPage() {
  const { t } = useTranslation();
  const { propertyIds } = useObjectScope();
  const canEdit = canManage(useAuth().user);
  const pricing = useDynamicPricingOverview(propertyIds);
  const problem = useApiProblem(pricing.query.error);
  const [confirming, setConfirming] = useState(false);
  const data = pricing.query.data;
  const control = data?.tenantControl;
  const facts = data ? headerFacts(data.apartments) : null;
  const killed = control?.isKilled ?? false;
  const state = data ? workspaceState(data) : null;
  const halted = state !== null && state !== 'running';
  const retry = () => void pricing.query.refetch();

  return (
    <div className="w-full min-w-0">
      <OverviewHeader
        subline={
          data && facts && state
            ? `${apartmentsLabel(data.apartments.length)} · ${state === 'running' ? `${facts.automatic} automatisch` : HALTED_TEXT[state]}`
            : 'Wie die Preisautomatik gerade steuert'
        }
        chips={data && facts && state ? headerChips(facts, state, data.apartments.length) : []}
        state={state}
        tenantKilled={killed}
        globalEnabled={data?.globalAutomationEnabled ?? true}
        onToggle={
          canEdit && control && !control.isMissing && control.concurrency != null
            ? () => setConfirming(true)
            : undefined
        }
      />
      <div className="relative z-10 -mt-[96px] flex flex-col gap-5 md:px-5">
        {pricing.updateError && (
          <p
            role="alert"
            className="bg-sealwax-tint text-sealwax rounded-tile p-4 text-sm font-semibold"
          >
            {problemText(pricing.updateError, 'Die Steuerung konnte nicht gespeichert werden.', {
              known: {
                dynamic_pricing_tenant_control_conflict:
                  'Die Steuerung wurde inzwischen geändert – die Übersicht zeigt jetzt den aktuellen Stand.',
              },
            })}
          </p>
        )}
        {data && pricing.query.error && (
          <SurfaceStaleHint onRetry={retry} retrying={pricing.query.isFetching} />
        )}
        {!data ? (
          <section className="bg-card border-hairline rounded-tile shadow-float overflow-hidden border">
            {pricing.query.error ? (
              <SurfaceLoadError
                message={problemText(
                  problem,
                  'Die Übersicht der Preisautomatik konnte nicht geladen werden.',
                )}
                onRetry={retry}
                retrying={pricing.query.isFetching}
              />
            ) : (
              <SurfaceLoading label="Übersicht wird geladen" rows={3} />
            )}
          </section>
        ) : data.apartments.length === 0 ? (
          <section className="bg-card border-hairline rounded-tile shadow-float border">
            <EmptyState icon="Building" title={t('admin.dynamicPricing.overview.empty')} />
          </section>
        ) : (
          <>
            <OverviewTodos items={todos(data.apartments)} />
            <OverviewApartments apartments={data.apartments} halted={halted} />
            <OverviewActivity
              apartments={data.apartments}
              globalWarnings={data.warningCodes}
              when={shortTime}
            />
          </>
        )}
      </div>
      <OverviewStopDialog
        open={confirming}
        onOpenChange={setConfirming}
        killed={killed}
        loading={pricing.isUpdating}
        onConfirm={() => {
          if (control?.concurrency == null) return;
          pricing.updateTenantControl(
            { isKilled: !killed, expectedConcurrency: control.concurrency },
            { onSettled: () => setConfirming(false) },
          );
        }}
      />
    </div>
  );
}
